import React from 'react';
import { Progress, Space, Typography, Tooltip } from 'antd';
import { FireOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';

const { Text } = Typography;

/**
 * クレジット表示コンポーネント
 *
 * @param {Object} creditInfo - クレジット情報
 * @param {number} creditInfo.plan_limit - プラン上限
 * @param {number} creditInfo.credit_used - 使用済み
 * @param {number} creditInfo.bonus_credit - ボーナスクレジット
 * @param {number} creditInfo.available_regular - 通常クレジット残量
 * @param {number} creditInfo.usage_rate - 使用率（0-1）
 * @param {boolean} showBonus - ボーナスクレジットを表示するか
 * @param {string} size - サイズ（'default' | 'small'）
 */
const CreditDisplay = ({ creditInfo, showBonus = true, size = 'default' }) => {
  if (!creditInfo) {
    return (
      <Space direction="vertical" size="small" style={{ width: '100%' }}>
        <Text
          type="secondary"
          style={{ fontSize: size === 'small' ? '12px' : '14px' }}
        >
          クレジット情報を読み込み中...
        </Text>
      </Space>
    );
  }

  const {
    plan_limit = 0,
    credit_used = 0,
    bonus_credit = 0,
    available_regular = 0,
    usage_rate = 0,
  } = creditInfo;

  const percentage = Math.round(usage_rate * 100);

  // プログレスバーの色（使用率に応じて変化）
  const getProgressColor = () => {
    if (percentage >= 90) return '#ff4d4f'; // 赤
    if (percentage >= 70) return '#faad14'; // オレンジ
    return '#52c41a'; // 緑
  };

  // 管理者の場合は無限大
  const isUnlimited = plan_limit === 999999999;
  const displayLimit = isUnlimited ? '∞' : plan_limit.toLocaleString();

  // 残量表示（管理者の場合は∞）
  const displayRemaining = isUnlimited
    ? '∞'
    : available_regular.toLocaleString();
  const remainingCredit = available_regular + bonus_credit;

  return (
    <Space direction="vertical" size={4} style={{ width: '100%' }}>
      {/* プログレスバーと数値を1行に */}
      <div
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        <Tooltip
          title={
            <div>
              <div>使用率: {percentage}%</div>
              <div style={{ marginTop: '4px' }}>
                残り: {displayRemaining} クレジット
              </div>
              {bonus_credit > 0 && (
                <div style={{ marginTop: '4px' }}>
                  ボーナス: +{bonus_credit.toLocaleString()} クレジット(無期限)
                </div>
              )}
            </div>
          }
          placement="bottom"
        >
          <div style={{ flex: 1, minWidth: '100px' }}>
            <Progress
              percent={percentage}
              strokeColor={getProgressColor()}
              showInfo={false}
              size={size === 'small' ? 'small' : 'default'}
              style={{ margin: 0 }}
            />
          </div>
        </Tooltip>

        <Space
          size={4}
          style={{
            fontSize: size === 'small' ? '12px' : '14px',
            flexShrink: 0,
          }}
        >
          <Text style={{ fontSize: 'inherit', fontWeight: 'bold' }}>
            {credit_used.toLocaleString()} / {displayLimit}
          </Text>

          {showBonus && bonus_credit > 0 && (
            <Tooltip title="ボーナスクレジット（無期限）" placement="right">
              <Text
                style={{
                  fontSize: size === 'small' ? '11px' : '13px',
                  fontWeight: 'bold',
                  background:
                    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                +{bonus_credit.toLocaleString()}
              </Text>
            </Tooltip>
          )}
        </Space>
      </div>

      {/* クレジット残量表示 */}
      <Text
        type="secondary"
        style={{ fontSize: size === 'small' ? '11px' : '12px' }}
      >
        残量: {isUnlimited ? '∞' : remainingCredit.toLocaleString()} クレジット
      </Text>
    </Space>
  );
};

CreditDisplay.propTypes = {
  creditInfo: PropTypes.shape({
    plan_limit: PropTypes.number,
    credit_used: PropTypes.number,
    bonus_credit: PropTypes.number,
    usage_rate: PropTypes.number,
  }),
  showBonus: PropTypes.bool,
  size: PropTypes.oneOf(['default', 'small']),
};

export default CreditDisplay;
